import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useStoreState, useStoreActions } from "easy-peasy";
import MuteButton from "../MuteButton";
import analytics from "../../utils/analytics";

const OutroScreen = ({ content, webResponsiveUtil, product }) => {
  const CONTAINER_ID = "interactiveExperienceOutro";
  const containerRef = useRef(null);
  const { title, subtitle, logo, background, replayBtn } = content;
  const { icon: replayIcon } = replayBtn;
  const { player: audioPlayer } = useStoreState((state) => state.bgm);
  const { player: videoPlayer } = useStoreState((state) => state.stage);
  const isOutroVisible = useStoreState((state) => state.outro.isVisible);
  const demoStartTime = useStoreState((state) => state.demo.startTime);

  const { setVisible: setDemoVisible } = useStoreActions(
    (actions) => actions.demo
  );
  const setOutroVisible = useStoreActions(
    (actions) => actions.outro.setVisible
  );
  const setTotalPlayed = useStoreActions(
    (actions) => actions.analytics.setTotalPlayed
  );
  const setCurrentIntervalStart = useStoreActions(
    (actions) => actions.analytics.setCurrentIntervalStart
  );
  const setCurrentIntervalEnd = useStoreActions(
    (actions) => actions.analytics.setCurrentIntervalEnd
  );
  const showOutro = () => {
    containerRef.current.classList.remove("is-fade-out");
  };

  const hideOutro = () => {
    containerRef.current.classList.add("is-fade-out");
  };

  const handleReplayClick = () => {
    setTotalPlayed(0);
    setCurrentIntervalStart(0);
    setCurrentIntervalEnd(0);
    setOutroVisible(false);
    if (product && product.name) {
      analytics.trackReplay(product.name);
    }
    if (videoPlayer) {
      videoPlayer.pause();
      videoPlayer.currentTime(demoStartTime);
    }
    if (audioPlayer) {
      audioPlayer.currentTime(0);
      audioPlayer.pause();
    }
    setTimeout(() => {
      setDemoVisible(true);
      if (videoPlayer) {
        videoPlayer.play();
      }
      if (audioPlayer) {
        audioPlayer.play();
      }
    }, 1500);
  };

  useEffect(() => {
    if (isOutroVisible) {
      showOutro();
    } else {
      hideOutro();
    }
    return () => {};
  }, [isOutroVisible]);

  return (
    <div ref={containerRef} id={CONTAINER_ID} className="is-fade-out">
      <div className="outro">
        <img
          src={background.url}
          className="visually-hidden outro__bg-image"
          alt={background.altText}
        />
        <div className="outro__content">
          {title.show && (
            <h2
              className="outro__title"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: title.text,
              }}
            />
          )}
          {subtitle.show && (
            <h3
              className="outro__subtitle"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: subtitle.text,
              }}
            />
          )}
        </div>
        <div className="outro__logo">
          <img className="outro-logo__img" src={logo.url} alt={logo.altText} />
        </div>
        {replayBtn.show && (
          <div className="outro__btn-container">
            <button
              type="button"
              className="outro__btn"
              onClick={handleReplayClick}
            >
              <span className="outro__btn-icon">
                <img
                  className="outro-btn__icon-img"
                  src={replayIcon.url}
                  alt={replayIcon.altText}
                />
              </span>
              <span className="outro__btn-label">{replayBtn.text}</span>
            </button>
            <MuteButton containerId={CONTAINER_ID} />
          </div>
        )}
      </div>
      <style jsx="true">
        {`
          #${CONTAINER_ID}{
            transition: all 0.5s ease-in-out !important;
            position: absolute !important;
            height: 100% !important;
            width: 100% !important;
          }
          #${CONTAINER_ID}.is-fade-out{
            opacity: 0 !important;
          }
          #${CONTAINER_ID} .outro {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;

            width: 100% !important;
            height: 100% !important;
            background-image: url(${background.url}) !important;
            background-color: #000000 !important;
            background-repeat: no-repeat !important;
            background-position: center center !important;
            background-size: cover !important;
          }
          
          #${CONTAINER_ID} .outro.is-visible {
            z-index: 1 !important;
          }

          #${CONTAINER_ID} .outro__content {
            left: ${webResponsiveUtil.getWidthValue(118)}px !important;
            top: ${webResponsiveUtil.getHeightValue(108)}px !important;

            position: absolute !important;
          }

          #${CONTAINER_ID} .outro__title {
            margin-bottom: ${webResponsiveUtil.getHeightValue(20)}px !important;
            font-size: ${webResponsiveUtil.getFontSize(57)}px !important;

            margin: 0 !important;
            padding: 0 !important;

            color: ${title.color} !important;
            font-family: "${title.font}", sans-serif !important;
            
          }

          #${CONTAINER_ID} .outro__title strong {
            font-weight: 600 !important;
          }
          
          #${CONTAINER_ID} .outro__subtitle {
            font-size: ${webResponsiveUtil.getFontSize(41)}px !important;

            font-weight: 300 !important;

            margin: 0 !important;
            padding: 0 !important;

            color: ${subtitle.color} !important;
            font-family: "${subtitle.font}", sans-serif !important;
          }

          #${CONTAINER_ID} .outro__btn-container {
            position: absolute !important;
            bottom: ${webResponsiveUtil.getHeightValue(100)}px !important;
            left: ${webResponsiveUtil.getWidthValue(118)}px !important;
            display: flex !important;
          }

          #${CONTAINER_ID} .outro__btn {
            padding-top: ${webResponsiveUtil.getHeightValue(10)}px !important;
            padding-bottom: ${webResponsiveUtil.getHeightValue(
              10
            )}px !important;
            padding-right: ${webResponsiveUtil.getWidthValue(45)}px !important;
            padding-left: ${webResponsiveUtil.getWidthValue(45)}px !important;
            border-radius: ${webResponsiveUtil.getWidthValue(25)}px !important;
            font-size: ${webResponsiveUtil.getFontSize(42)}px !important;
            margin: 0 !important;
            display: flex !important;
            border: solid ${webResponsiveUtil.getWidthValue(
              2
            )}px #ffffff !important;
            background: ${replayBtn.bgColor || "transparent"} !important;

            align-items: center !important;
            justify-content: center !important;
            color: ${replayBtn.color} !important;
            font-family: "${replayBtn.font}", sans-serif !important;
            box-shadow: none !important;
          }

          #${CONTAINER_ID} .outro__btn:hover,
          #${CONTAINER_ID} .outro__btn:hover * {
            cursor: pointer !important;
          }
          
          #${CONTAINER_ID} .outro__btn:focus,
          #${CONTAINER_ID} .outro__btn:active {
              outline: 0 !important;
          }

          #${CONTAINER_ID} .outro__btn:active {
            opacity: 0.5 !important;
          }
          #${CONTAINER_ID} .outro__btn-icon {
            height: ${webResponsiveUtil.getHeightValue(55)}px !important;
            width: ${webResponsiveUtil.getWidthValue(45)}px !important;
            margin-right: ${webResponsiveUtil.getWidthValue(20)}px !important;

            display: inline-block !important;
          }
          #${CONTAINER_ID} .outro__btn-icon svg,
          #${CONTAINER_ID} .outro__btn-icon img {
            display: block !important;

            width: 100% !important;
            height: 100% !important;
          }

          #${CONTAINER_ID} .outro__btn-label {
            font-size: ${webResponsiveUtil.getFontSize(42)}px !important;
            margin-top: ${webResponsiveUtil.getHeightValue(8)}px !important;

            font-weight: 400 !important;

            display: inline-block !important;
            font-family: "${replayBtn.font}", sans-serif !important;
            color: ${replayBtn.color} !important;
          }

          #${CONTAINER_ID} .outro__logo {
            width: ${webResponsiveUtil.getWidthValue(242)}px !important;
            height: ${webResponsiveUtil.getHeightValue(54)}px !important;
            top: ${webResponsiveUtil.getHeightValue(109)}px !important;
            right: ${webResponsiveUtil.getWidthValue(122)}px !important;

            position: absolute !important;
          }
          #${CONTAINER_ID} .outro__logo svg,
          #${CONTAINER_ID} .outro__logo img {
            display: block !important;

            width: 100% !important;
            height: 100% !important;
          }
        `}
      </style>
    </div>
  );
};

OutroScreen.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.shape({
      text: PropTypes.string,
      font: PropTypes.string,
      color: PropTypes.string,
      show: PropTypes.bool,
    }),
    subtitle: PropTypes.shape({
      text: PropTypes.string,
      font: PropTypes.string,
      color: PropTypes.string,
      show: PropTypes.bool,
    }),
    logo: PropTypes.shape({
      url: PropTypes.string,
      altText: PropTypes.string,
    }),
    background: PropTypes.shape({
      url: PropTypes.string,
      altText: PropTypes.string,
    }),
    replayBtn: PropTypes.shape({
      show: PropTypes.bool,
      text: PropTypes.string,
      font: PropTypes.string,
      color: PropTypes.string,
      bgColor: PropTypes.string,
      icon: PropTypes.shape({
        url: PropTypes.string,
        altText: PropTypes.string,
      }),
    }),
  }),
  webResponsiveUtil: PropTypes.shape({
    getHeightValue: PropTypes.func,
    getWidthValue: PropTypes.func,
    getFontSize: PropTypes.func,
  }),
  product: PropTypes.shape({
    name: PropTypes.string,
  }),
};

OutroScreen.defaultProps = {
  content: {
    title: {
      text: "<strong>Roomba s Series</strong>",
      font: "HarmoniaSans",
      color: "#FFFFFFF",
    },
    subtitle: {
      text: "with the new iRobot Home App",
      font: "HarmoniaSans",
      color: "#FFFFFF",
    },
    logo: {
      url: "../../images/outro/icon-logo.svg",
      altText: "iRobot Logo",
    },
    background: {
      url: "../../images/outro/outro-bg.jpg",
      altText: "Outro S9 and app",
    },
    replayBtn: {
      showReplayBtn: true,
      text: "Replay",
      font: "HarmoniaSans",
      color: "#FFFFFF",
      bgColor: "transparent",
      icon: {
        url: "../../images/outro/icon-replay.svg",
        altText: "Relive the experience",
      },
    },
  },

  webResponsiveUtil: {
    getHeightValue: () => {},
    getWidthValue: () => {},
    getFontSize: () => {},
  },
  product: {
    name: "",
  },
};

export default OutroScreen;
